/**
 * The shell's loading state: a skeleton of the layout that is about to appear.
 *
 * WHAT IT REPLACES, AND WHY. The session check used to render the word
 * "Loading…" centred on the page with `themed-text-muted` on `themed-bg-primary`
 * — classes whose CSS variables are defined ONLY under `.theme-dark` /
 * `.theme-dim` / `.theme-light`. That gate renders above `ThemedArea`, so no
 * ancestor carried a theme class, both variables resolved to nothing, and the
 * result was unstyled black text on an unpainted background — the one
 * combination that looks broken in dark mode. (CLAUDE.md → "Components Outside
 * ThemedArea".) So this component applies the theme class itself, exactly as a
 * modal or portal must.
 *
 * Beyond the colour fix, a skeleton is the honest thing to show here. The
 * session check is short but not instant, and the layout that follows is
 * completely predictable — rail, top bar, side nav, content card. Drawing that
 * shape means the shell FILLS IN rather than replacing a centred word, so
 * nothing jumps and the wait reads as progress instead of a stall.
 *
 * DELIBERATELY NOT BRANDED. No logo, no product name: this renders while the
 * session — and on a cold cache the brand itself — is still resolving, and a
 * default-brand logo flashing before the real one is worse than no logo. The
 * geometry is the message.
 *
 * Kept in sync with apps/web/src/App.tsx's shell by SHAPE, not by sharing
 * components: it must not mount the real TaskBar/DriveShell (they need data
 * this screen exists precisely because we do not have yet). If the shell's
 * proportions change, the numbers here are approximations that can drift a
 * little without hurting — what must stay true is rail-left, bar-top,
 * nav-then-content.
 */
import { useTheme, getThemeClass } from './ThemeProvider'

/** One pulsing placeholder block. `.skeleton` (apps/web/src/index.css) paints
 *  it from the active theme's surface variable and animates the pulse. */
function Block({ className = '', style }: { className?: string; style?: React.CSSProperties }) {
  return <div className={`skeleton ${className}`} style={style} />
}

export function AppShellSkeleton({ label = 'Loading' }: {
  /** Announced to assistive tech. The visible text is gone, so this is the
   *  only thing a screen reader has — pass the translated string. */
  label?: string
}) {
  const { settings } = useTheme()
  const themeClass = getThemeClass(settings.theme.workspace)

  return (
    <div
      className={`h-screen flex overflow-hidden ${themeClass} ws-stage relative isolate`}
      role="status"
      aria-busy="true"
      aria-live="polite"
      // Not decoration: this is what "the shell is still waiting" looks like,
      // and e2e/landing-switch.spec.ts asserts it POSITIVELY — with the landing
      // response held open, this staying on screen is the proof that no surface
      // was painted from a stale cache. `role=status` is shared with toasts, so
      // a role matcher would be ambiguous.
      data-testid="app-shell-skeleton"
    >
      <span className="sr-only">{label}</span>

      {/* Taskbar rail — vertical, w-14, mark at the top, avatar at the bottom. */}
      <div className="w-14 ws-taskbar flex flex-col items-center py-2 gap-1 shrink-0" aria-hidden>
        <Block className="w-10 h-10 mt-1 mb-2 rounded-lg" />
        <Block className="w-10 h-10 rounded-lg" />
        <Block className="w-10 h-10 rounded-lg" />
        <Block className="w-10 h-10 rounded-lg" />
        <div className="w-8 border-t themed-border-secondary my-1" />
        <Block className="w-10 h-10 rounded-lg" />
        <div className="flex-1" />
        <Block className="w-9 h-9 rounded-full" />
      </div>

      {/* Content column: the permanent top bar, then the window. */}
      <div className="flex-1 flex flex-col min-w-0 min-h-0 overflow-hidden pt-1.5" aria-hidden>
        <div className="ws-topbar-slot flex items-center gap-2 px-3">
          <Block className="h-3 w-40 rounded" />
          <div className="flex-1" />
          <Block className="h-5 w-5 rounded" />
          <Block className="h-5 w-5 rounded" />
        </div>

        <div className="flex-1 flex min-w-0 min-h-0 overflow-hidden px-3 pb-3 pt-0.5 gap-3">
          {/* Side nav card */}
          <div className="ws-card w-52 shrink-0 flex flex-col py-3 px-2">
            <div className="px-3 pb-3 mb-1 border-b themed-border-secondary">
              <Block className="h-3.5 w-24 rounded" />
              <Block className="h-2.5 w-16 rounded mt-1.5" />
            </div>
            <div className="flex flex-col gap-1.5 px-1 pt-1">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="flex items-center gap-2 px-2 py-1.5">
                  <Block className="h-4 w-4 rounded" />
                  <Block className="h-2.5 rounded" style={{ width: `${[68, 52, 74, 44][i]}%` }} />
                </div>
              ))}
            </div>
            <div className="flex-1" />
            <div className="flex flex-col gap-1.5 px-1 pt-2 border-t themed-border-secondary">
              {[0, 1].map((i) => (
                <div key={i} className="flex items-center gap-2 px-2 py-1.5">
                  <Block className="h-4 w-4 rounded" />
                  <Block className="h-2.5 rounded" style={{ width: i ? '62%' : '38%' }} />
                </div>
              ))}
            </div>
          </div>

          {/* Hero content card */}
          <div className="ws-card-hero flex-1 min-w-0 flex flex-col overflow-hidden p-5">
            <Block className="h-5 w-48 rounded" />
            <Block className="h-3 w-64 rounded mt-2.5" />
            <div className="grid gap-3 mt-6" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))' }}>
              {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
                <div key={i} className="rounded-lg border themed-border-secondary p-3">
                  <Block className="h-9 w-9 rounded" />
                  <Block className="h-2.5 w-full rounded mt-3" />
                  <Block className="h-2.5 w-2/3 rounded mt-1.5" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
